import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '../d-me-organizations';
import 'src/library/elements/d-smooth-resize';
import 'src/library/fields/d-view-html';
import { ContactDialog } from 'src/outskirts/account/emergency/contact-dialog';
import type {
  EmergencyCauseCategory,
  EmergencyContact,
  EmergencyMeasuresCard,
  EmergencyViewModel,
} from 'src/outskirts/account/emergency/emergency-view-model';
import './d-emergency-measures-card';
import { emergencyCauseCategories } from 'src/store/data/emergency-cause-categories';
import { classMap } from 'lit/directives/class-map.js';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';
import { AlertDialog } from 'src/library/components/alert-dialog';
import { handleNavigation } from 'src/redux-behavior';
import { t } from 'src/i18n';

/**
 * Simplified and focused UI for emergencyStates
 */
@customElement('d-emergency')
export class DEmergency extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      height: 100vh;
      width: 100vw;
      max-width: 600px;
      margin: 0 auto;
      overflow: hidden;
    }
    :host > * {
      line-height: normal;
    }
    #sections {
      display: flex;
      width: 100%;
      transition: margin-left 0.3s;
    }
    :host([selected-card]:not([selected-card=''])) #sections {
      margin-left: -100%;
    }
    section {
      flex: none;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100vh;
      padding: 20px 0 0;
      box-sizing: border-box;
      background-color: var(--emergency-background-color);
    }
    header {
      flex: none;
      position: relative;
      background-color: var(--emergency-background-color);
      margin: 0 20px 20px;
    }
    .back {
      position: absolute;
      top: -20px;
      left: -20px;
      width: 60px;
      height: 70px;
      background: url(/images/back-arrow-orange.svg) 50% 50% no-repeat;
      background-size: 40px auto;
      cursor: pointer;
    }
    .title {
      padding: 0 36px;
      font-size: 24px;
      font-weight: 500;
      color: var(--emergency-accent-color);
      text-align: center;
    }
    .state-title {
      color: white;
    }
    .phone {
      position: absolute;
      top: -20px;
      right: -20px;
      width: 60px;
      height: 70px;
      background: url(/images/phone-orange.svg) 0 50% no-repeat;
      background-size: 40px auto;
      cursor: pointer;
    }
    .organization {
      margin-top: 6px;
      font-size: 20px;
      color: white;
      text-align: center;
    }
    .contacts {
      color: var(--emergency-accent-color);
      font-family: var(--main-sans-narrow), sans-serif;
    }
    .contacts-menu {
      display: flex;
      flex-wrap: wrap;
      padding: 0 20px;
      margin: 0 -6px;
      font-size: 15px;
      color: var(--emergency-accent-color);
    }
    .contacts-menu > div {
      padding: 10px 6px;
      cursor: pointer;
    }
    .contacts-menu > div.selected {
      color: white;
    }
    .contacts-content {
      max-height: 50vh;
      overflow: auto;
      padding: 0 20px;
    }
    .contact {
      border-top: 1px solid var(--emergency-accent-color-semi-transparent);
      padding: 12px 0;
      cursor: pointer;
    }
    .contact .name {
      font-size: 20px;
      font-weight: 500;
    }
    .partner-organization {
      margin-top: 4px;
      font-size: 16px;
      font-weight: normal;
      font-family: var(--mainSans), sans-serif;
    }
    main {
      flex-grow: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding-bottom: 180px;
    }
    .menu-item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      margin: 0 20px;
      border-top: 1px solid var(--emergency-accent-color-semi-transparent);
      padding: 16px 0;
      font-size: 20px;
      color: white;
      cursor: pointer;
    }
    .menu-item div {
      padding-right: 20px;
    }
    .menu-item:after {
      flex: none;
      content: '';
      right: 0;
      width: 12px;
      height: 12px;
      border-top: 3px solid var(--emergency-accent-color);
      border-right: 3px solid var(--emergency-accent-color);
      transform: rotate(45deg);
      border-radius: 2px;
    }
    .menu-item:last-child {
      border-bottom: 1px solid var(--emergency-accent-color-semi-transparent);
    }
    .menu-item.damped > * {
      opacity: 0.5;
      font-weight: 200;
    }
    footer {
      position: fixed;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      width: 100vw;
      height: 0;
      overflow: visible;
    }
    .footer-content {
      background: linear-gradient(
        0deg,
        var(--emergency-background-color) 75%,
        var(--emergency-background-color-transparent) 100%
      );
    }
    .categories {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      padding: 20px 20px 10px 20px;
      text-align: center;
      color: var(--emergency-accent-color);
    }
    .categories > div {
      padding: 4px 6px;
      cursor: pointer;
    }
    .categories > div.all {
      width: 100%;
    }
    .categories > div.selected {
      color: white;
    }
    .cancel {
      box-sizing: border-box;
      height: 40px;
      margin-bottom: 20px;
      background-color: var(--emergency-background-color);
      border-radius: 20px;
      padding: 10px 12px 12px;
      font-size: 16px;
      text-align: center;
      color: white;
      cursor: pointer;
    }
    d-emergency-measures-card {
      margin: 0 20px;
    }
  `;

  @property({ type: Object })
  viewModel!: EmergencyViewModel;
  @property({ type: Boolean })
  isAppPlatform = false;
  @query('#sections')
  sections!: HTMLElement;
  @property({ type: String, attribute: 'selected-card', reflect: true })
  selectedCardId = '';
  @property({ type: String })
  selectedCategory = '';
  @property({ type: Boolean })
  contactsOpen = false;
  @property({ type: Number })
  selectedContactsList = 1;

  private get selectedCard(): EmergencyMeasuresCard | undefined {
    return this.viewModel.measuresCards.find((c) => c.uuid === this.selectedCardId);
  }

  private situationIsEmpty(situation: EmergencyMeasuresCard) {
    return situation.userFunctions.length + situation.otherFunctions.length === 0;
  }

  private toggleContacts() {
    this.contactsOpen = !this.contactsOpen;
  }

  cancelEmergency() {
    this.dispatchEvent(
      new CustomEvent('update-emergency-settings', {
        bubbles: true,
        composed: true,
        detail: { isUserEmergencyMode: false },
      }),
    );
  }

  private selectCard(uuid: string) {
    this.selectedCardId = uuid;
    this.contactsOpen = false;
    localStorage.setItem('emergencyMeasuresCard', uuid);
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    const storedCard = localStorage.getItem('emergencyMeasuresCard');
    if (storedCard) {
      const card = this.viewModel.measuresCards.find((c) => c.uuid === storedCard);
      if (card) {
        this.selectedCardId = storedCard;
      } else {
        localStorage.removeItem('emergencyMeasuresCard');
      }
    }
  }

  private navigateHome() {
    this.removeAttribute('selected-card');
    this.contactsOpen = false;
    setTimeout(() => {
      this.selectedCardId = '';
    }, 300);
  }

  private async openContactDialog(contact: EmergencyContact) {
    if (contact.phone || contact.email) {
      await ContactDialog.open({
        name: contact.name,
        organizationName: contact.organizationName,
        phone: contact.phone,
        email: contact.email,
        styling: 'emergency',
        isAppPlatform: this.isAppPlatform,
      });
    }
  }

  private async followInternalLink(href: string) {
    window.history.pushState({}, '', href);
    await handleNavigation(window.location);
    this.cancelEmergency();
  }

  private async onProcedureLinkClick(e: CustomEvent<{ docType: string; uuid: string; href: string }>) {
    e.stopPropagation();
    const docType = e.detail.docType;
    const uuid = e.detail.uuid;
    const href = e.detail.href;
    if (docType === 'partners' || docType === 'contacts' || docType === 'employees') {
      const listMap = {
        partners: this.viewModel.partners,
        contacts: this.viewModel.contacts,
        employees: this.viewModel.employees,
      };
      const contact = listMap[docType].find((item) => item.uuid === uuid);
      if (contact) {
        return this.openContactDialog(contact);
      }
    } else {
      const dontShowLinkAlert = localStorage.getItem('emergencyMeasuresCardDontShowLinkAlert');
      let skipLinkAlert = false;
      try {
        skipLinkAlert = dontShowLinkAlert !== null && JSON.parse(dontShowLinkAlert) === true;
      } catch {
        localStorage.removeItem('emergencyMeasuresCardDontShowLinkAlert');
      }
      if (skipLinkAlert) {
        return this.followInternalLink(href);
      } else {
        const result: AlertDialogResult = await AlertDialog.open({
          title: t('emergency.openDocumentTitle'),
          content: t('emergency.openDocumentMessage'),
          dontShowAgainOption: true,
        });
        if (result.action === 'ok') {
          if (result.dontShowAgain) {
            localStorage.setItem('emergencyMeasuresCardDontShowLinkAlert', 'true');
          }
          return this.followInternalLink(href);
        }
      }
    }
  }

  private toggleCategory(category: string) {
    if (this.selectedCardId === '') {
      if (this.selectedCategory === category) {
        this.selectedCategory = '';
      } else {
        this.selectedCategory = category;
      }
    }
  }

  private categoryClass(category: EmergencyCauseCategory | 'all') {
    if (this.selectedCardId === '') {
      return {
        all: category === 'all',
        selected: this.selectedCategory === category || (category === 'all' && this.selectedCategory === ''),
      };
    }
    return {
      all: false,
      selected:
        this.selectedCard !== undefined && category !== 'all' && this.selectedCard.causeCategories.includes(category),
    };
  }

  private menuItemClass(categories: string[]) {
    return {
      damped: !categories.includes(this.selectedCategory) && this.selectedCategory !== '',
    };
  }

  private get relatedContacts(): EmergencyContact[] {
    if (this.selectedContactsList === 1) {
      if (this.selectedCardId) {
        const card = this.viewModel.measuresCards.find((c) => c.uuid === this.selectedCardId);
        if (card) {
          const seenIds = new Set();
          const combined = card.contactsInContent.concat(card.assignedEmployees, this.viewModel.emergencyContacts);
          const unique = combined.filter((item) => {
            if (seenIds.has(item.uuid)) {
              return false;
            }
            seenIds.add(item.uuid);
            return true;
          });
          return unique;
        }
      }
      return this.viewModel.emergencyContacts;
    }
    if (this.selectedContactsList === 2) {
      return this.viewModel.employees;
    }
    return this.viewModel.partnersAndContacts;
  }

  contactsMenuClass(n: number) {
    return {
      selected: n === this.selectedContactsList,
    };
  }

  private renderContacts() {
    if (this.contactsOpen) {
      return html`
        <div class="contacts">
          <div class="contacts-menu">
            <div class=${classMap(this.contactsMenuClass(1))} @click=${() => (this.selectedContactsList = 1)}>
              Mest relevante
            </div>
            <div class=${classMap(this.contactsMenuClass(2))} @click=${() => (this.selectedContactsList = 2)}>
              Personale
            </div>
            <div class=${classMap(this.contactsMenuClass(3))} @click=${() => (this.selectedContactsList = 3)}>
              Samarbeidspartnere
            </div>
          </div>
          <div class="contacts-content">
            ${this.relatedContacts.map((contact) => {
              return html`
                <div class="contact" @click=${() => this.openContactDialog(contact)}>
                  <div class="name">${contact.name}</div>
                  ${contact.organizationName !== ''
                    ? html` <div class="partner-organization">${contact.organizationName}</div> `
                    : nothing}
                </div>
              `;
            })}
          </div>
        </div>
      `;
    }
    return nothing;
  }

  private renderCardSection() {
    if (this.selectedCard) {
      return html`
        <section>
          <header>
            <div class="back" @click=${() => this.navigateHome()}></div>
            <div class="title state-title">${this.selectedCard.name}</div>
            <div class="phone" @click=${() => this.toggleContacts()}></div>
          </header>
          <d-smooth-resize> ${this.renderContacts()}</d-smooth-resize>
          <main>
            <d-emergency-measures-card
              .isAppPlatform=${this.isAppPlatform}
              .card=${this.selectedCard}
              .empty=${this.situationIsEmpty(this.selectedCard)}
              @link-click=${(e: CustomEvent<{ docType: string; uuid: string; href: string }>) =>
                this.onProcedureLinkClick(e)}
            ></d-emergency-measures-card>
          </main>
        </section>
      `;
    }
    return nothing;
  }

  render() {
    return html`
      <div id="sections">
        <section>
          <header>
            <div class="title">${t('emergency.crisisHandling')}</div>
            <div class="organization">${this.viewModel.organizationName}</div>
            <div class="phone" @click=${() => this.toggleContacts()}></div>
          </header>
          <d-smooth-resize> ${this.renderContacts()}</d-smooth-resize>
          <main>
            ${this.viewModel.measuresCards.map((e) => {
              return html` <div
                class="menu-item ${classMap(this.menuItemClass(e.causeCategories))}"
                @click=${() => this.selectCard(e.uuid)}
              >
                <div>${e.name}</div>
              </div>`;
            })}
          </main>
        </section>
        ${this.renderCardSection()}
      </div>
      <footer>
        <div class="footer-content">
          <div class="categories">
            ${this.selectedCardId === ''
              ? html`
                  <div class=${classMap(this.categoryClass('all'))} @click=${() => (this.selectedCategory = '')}>
                    ${t('emergency.allCauses')}
                  </div>
                `
              : nothing}
            ${emergencyCauseCategories.map((c) => {
              return html`<div
                class=${classMap(this.categoryClass(c.value))}
                @click=${() => this.toggleCategory(c.value)}
              >
                ${c.text}
              </div>`;
            })}
          </div>
          <div class="cancel" @click=${() => this.cancelEmergency()}>${t('emergency.endCrisisHandling')}</div>
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-emergency': DEmergency;
  }
}
